import { useState } from "react";
import { Button, cn } from "@jem-hr/jem-ui";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CalendarEvent {
  date: number;
  title: string;
}

const SAMPLE_EVENTS: CalendarEvent[] = [
  { date: 6, title: "Jem Regression Test" },
  { date: 13, title: "Jem Regression Test" },
  { date: 20, title: "Jem Regression Test (..." },
  { date: 27, title: "Jem Regression Test" },
];

const DAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export function ReconCalendar() {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date(2026, 1, 1)); // February 2026

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() === month;
  const todayDate = isCurrentMonth ? today.getDate() : -1;

  const events = year === 2026 && month === 1 ? SAMPLE_EVENTS : [];

  function goToToday() {
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
  }

  function goToPrevMonth() {
    setCurrentDate(new Date(year, month - 1, 1));
  }

  function goToNextMonth() {
    setCurrentDate(new Date(year, month + 1, 1));
  }

  // Build calendar grid cells
  const prevMonthDays = getDaysInMonth(year, month - 1);
  const cells: { day: number; inMonth: boolean; isToday: boolean }[] = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, inMonth: false, isToday: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, inMonth: true, isToday: d === todayDate });
  }
  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    for (let d = 1; d <= remaining; d++) {
      cells.push({ day: d, inMonth: false, isToday: false });
    }
  }

  return (
    <div>
      {/* Header */}
      <div
        className="flex items-center justify-between -mx-xl -mt-lg px-xl py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <div>
          <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
            Recons
          </h1>
          <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
            View and manage scheduled reconciliation events.
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-md">
        <div className="flex items-center gap-xxs">
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goToPrevMonth}>
            <ChevronLeft size={16} />
          </Button>
          <Button variant="outline" size="sm" onClick={goToNextMonth}>
            <ChevronRight size={16} />
          </Button>
          <h2 className="text-lg font-semibold text-greyscale-text-title ml-xs">
            {MONTH_NAMES[month]} {year}
          </h2>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="border border-greyscale-border rounded-lg overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7">
          {DAY_HEADERS.map((day) => (
            <div
              key={day}
              className="py-xs px-xs text-xs font-semibold text-greyscale-text-caption text-center border-b border-greyscale-border"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Date cells */}
        <div className="grid grid-cols-7">
          {cells.map((cell, idx) => {
            const dayEvents = cell.inMonth
              ? events.filter((e) => e.date === cell.day)
              : [];

            return (
              <div
                key={idx}
                className={cn(
                  "min-h-[100px] border-b border-r border-greyscale-border p-xs",
                  idx % 7 === 0 && "border-l-0",
                  cell.isToday && "bg-secondary-pink-50"
                )}
              >
                <div className="flex justify-end">
                  <span
                    className={cn(
                      "text-sm",
                      !cell.inMonth && "text-greyscale-text-disabled",
                      cell.inMonth &&
                        !cell.isToday &&
                        "text-greyscale-text-body",
                      cell.isToday &&
                        "text-secondary-pink-900 font-semibold"
                    )}
                  >
                    {cell.day}
                  </span>
                </div>
                {dayEvents.map((event, eIdx) => (
                  <div
                    key={eIdx}
                    className="mt-xxs bg-primary-navy-900 text-white text-xs rounded px-xxs py-[2px] truncate"
                  >
                    {event.title}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
